@file:OptIn(ExperimentalJsExport::class)

package com.siriusxm.cmc

import kotlin.time.Duration

public typealias NotificationType = String

public object NotificationTypes {
    public const val Info: NotificationType = "Info"
    public const val Warning: NotificationType = "Warning"
    public const val Error: NotificationType = "Error"
    public const val Success: NotificationType = "Success"
}

/**
 * The interface to publish notifications to the container.
 */
@JsExport
public interface Notifications {
    /**
     * Display a notification.
     *
     * A notification with an [Notification.id] replaces any visible notification with the same id
     * (and restarts its auto-dismiss timer), so a caller that reports a recurring condition under a
     * stable id never stacks duplicates.
     */
    public fun show(notification: Notification)

    /**
     * Remove the notification with the given [id], if it is still visible. A no-op otherwise, so it
     * is safe to call when the condition clears whether or not the viewer already closed the toast.
     *
     * Defaults to a no-op so existing implementers keep compiling; an implementer that doesn't
     * override it will leave the toast on screen.
     */
    public fun dismiss(id: String) {}
}

@JsExport
public data class Notification(
    /**
     * This is a string and not an enum due to boundary issues with JsExport.
     */
    val style: NotificationType,
    val message: String,
    val description: String? = null,
    val id: String? = null,
    /**
     * The number of milliseconds to delay before automatically dismissing.
     */
    val dismiss: Int? = null
)

public fun Notifications.error(
    message: String,
    description: String? = null,
    autoDismiss: Duration? = null,
    id: String? = null
): Unit =
    show(
        Notification(
            NotificationTypes.Error, message, description, id,
            autoDismiss?.inWholeMilliseconds?.toInt()
        )
    )

public fun Notifications.info(message: String, description: String? = null, autoDismiss: Duration? = null): Unit =
    show(
        Notification(
            NotificationTypes.Info, message, description, null,
            autoDismiss?.inWholeMilliseconds?.toInt()
        )
    )

public fun Notifications.success(message: String, description: String? = null, autoDismiss: Duration? = null): Unit =
    show(
        Notification(
            NotificationTypes.Success, message, description, null,
            autoDismiss?.inWholeMilliseconds?.toInt()
        )
    )

public fun Notifications.warning(message: String, description: String? = null, autoDismiss: Duration? = null): Unit =
    show(
        Notification(
            NotificationTypes.Warning, message, description, null,
            autoDismiss?.inWholeMilliseconds?.toInt()
        )
    )